import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { EmptyState, StatusChip } from '../../components/ui';
import { publicPaymentsService } from '../../services/api';
import { PublicInvoiceView } from '../../types';
import { useSquareWebPayments } from '../../hooks/useSquareWebPayments';
import { useStripePaymentElement } from '../../hooks/useStripePaymentElement';

const money = (value: number | string | null | undefined) => {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : '$0.00';
};

/**
 * Public pay page, reached from the "Pay online" button in the invoice email.
 * Transactional, so calm-dashboard styling — no scroll reveals or parallax.
 * The server charges the outstanding balance; no amount is ever sent from here.
 */
const PayInvoicePage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [view, setView] = useState<PublicInvoiceView | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [justPaid, setJustPaid] = useState(false);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    try {
      setLoading(true);
      setView(await publicPaymentsService.getPayView(invoiceId));
    } catch (err: any) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(err.response?.data?.message || 'Failed to load this invoice');
      }
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    load();
  }, [load]);

  const balanceSettled = (view?.balanceDue ?? 0) <= 0.005;
  const showCardForm = Boolean(view?.payment.enabled && !balanceSettled);
  // Older API responses carry no provider; they are always Square.
  const processor = view?.payment.provider ?? 'square';

  // Both hooks are always called; only the shop's processor is enabled.
  const square = useSquareWebPayments({
    applicationId: view?.payment.applicationId,
    locationId: view?.payment.locationId,
    environment: view?.payment.environment,
    enabled: showCardForm && processor === 'square',
  });
  const stripe = useStripePaymentElement({
    invoiceId,
    publishableKey: view?.payment.publishableKey,
    enabled: showCardForm && processor === 'stripe',
  });
  const { ready, loadError } = processor === 'stripe' ? stripe : square;

  // A redirect-based Stripe method (bank, wallet) returns here with the intent
  // id in the query string; the server checks it with Stripe.
  useEffect(() => {
    const returned = new URLSearchParams(window.location.search).get('payment_intent');
    if (!invoiceId || !returned) return;
    publicPaymentsService
      .confirmStripePayment(invoiceId, returned)
      .then((updated) => {
        setView(updated);
        setJustPaid(updated.balanceDue <= 0.005);
      })
      .catch(() => undefined); // the webhook settles it regardless
  }, [invoiceId]);

  const handlePay = async () => {
    if (!invoiceId) return;
    try {
      setPaying(true);
      setError(null);
      const updated =
        processor === 'stripe'
          ? await publicPaymentsService.confirmStripePayment(invoiceId, await stripe.confirm())
          : await publicPaymentsService.pay(invoiceId, { sourceId: await square.tokenize() });
      setView(updated);
      setJustPaid(updated.balanceDue <= 0.005);
      if (updated.balanceDue > 0.005) {
        setError('Your payment is processing — this page will show it once your bank confirms.');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          (err instanceof Error ? err.message : 'The payment could not be completed'),
      );
    } finally {
      setPaying(false);
    }
  };

  if (notFound) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6, mb: 6 }}>
        <EmptyState
          icon={<ReceiptLongIcon />}
          title="Invoice not found"
          description="This pay link is invalid or the invoice is no longer available. Contact the shop if you believe this is a mistake."
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 6, mb: 6 }}>
      {loading || !view ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Skeleton width="40%" height={36} />
          <Skeleton width="60%" />
          <Skeleton variant="rectangular" height={180} sx={{ mt: 2, borderRadius: 1 }} />
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Stack spacing={2.5}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" fontWeight={700}>
                  {view.shop.name || 'Invoice'}
                </Typography>
                <StatusChip status={view.status} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {view.ref}
                {view.vehicle ? ` • ${view.vehicle}` : ''}
              </Typography>
            </Box>

            {view.customerFirstName && !balanceSettled && (
              <Typography variant="body2" color="text.secondary">
                Hi {view.customerFirstName}, here's the balance for the work we completed.
              </Typography>
            )}

            <Divider />

            <Stack spacing={1}>
              {view.items.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{item.description}</Typography>
                    {item.quantity !== 1 && (
                      <Typography variant="caption" color="text.secondary">
                        {item.quantity} × {money(item.unitPrice)}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                    {money(item.lineTotal)}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Divider />

            <Stack spacing={0.5}>
              <Row label="Subtotal" value={money(view.subtotal)} muted />
              {view.taxes.map((tax, index) => (
                <Row
                  key={index}
                  label={`${tax.name} (${(Number(tax.rate) * 100).toFixed(2)}%)`}
                  value={money(tax.amount)}
                  muted
                />
              ))}
              <Row label="Total" value={money(view.amountDue)} strong />
              {view.amountPaid > 0 && (
                <>
                  <Row label="Amount paid" value={money(view.amountPaid)} muted />
                  <Row label="Balance due" value={money(view.balanceDue)} strong />
                </>
              )}
            </Stack>

            {balanceSettled ? (
              <Alert
                icon={<CheckCircleOutlineIcon />}
                severity="success"
                sx={{ alignItems: 'center' }}
              >
                {justPaid
                  ? `Payment received — thank you! A receipt will be emailed to you.`
                  : 'This invoice is paid in full. No action needed.'}
              </Alert>
            ) : !view.payment.enabled ? (
              <Alert severity="info">
                This invoice is settled at the shop
                {view.shop.phoneNumber ? ` — call ${view.shop.phoneNumber} with any questions` : ''}.
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Pay by card</Typography>
                {loadError ? (
                  <Alert severity="error">{loadError}</Alert>
                ) : (
                  <>
                    {processor === 'stripe' ? (
                      <div id="stripe-payment-element" />
                    ) : (
                      <div id="square-card-container" />
                    )}
                    {!ready && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={22} />
                      </Box>
                    )}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handlePay}
                      disabled={!ready || paying}
                    >
                      {paying ? 'Processing…' : `Pay ${money(view.balanceDue)}`}
                    </Button>
                  </>
                )}
              </Stack>
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {(view.shop.address || view.shop.phoneNumber) && (
              <Typography variant="caption" color="text.secondary">
                {[view.shop.address, view.shop.phoneNumber].filter(Boolean).join(' • ')}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}
    </Container>
  );
};

const Row: React.FC<{ label: string; value: string; muted?: boolean; strong?: boolean }> = ({
  label,
  value,
  muted,
  strong,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography
      variant={strong ? 'subtitle2' : 'body2'}
      color={muted ? 'text.secondary' : 'text.primary'}
    >
      {label}
    </Typography>
    <Typography variant={strong ? 'subtitle2' : 'body2'}>{value}</Typography>
  </Box>
);

export default PayInvoicePage;
